import { Box, Button, Typography, useTheme } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlatformError from '../../api/helpers/PlatformError'
import { GenerateCodeResponse } from '../../api/v1/verification/usePostGenerateCode'
import { AlertType, useAlert } from '../../providers/alert'

type GenerateCodeButtonParams = {
  data: GenerateCodeResponse | null
  loading: boolean
  error: PlatformError | null
  callFetch: () => void
  headers: Response['headers'] | null
}

const GenerateCodeButton = ({
  data,
  loading,
  error,
  callFetch,
  headers,
}: GenerateCodeButtonParams) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation('vpc', { keyPrefix: 'confirmEmail' })
  const theme = useTheme()
  const [countDown, setCountDown] = useState(0)
  const [countDownInterval, setCountDownInterval] = useState(null as NodeJS.Timeout | null)
  const [recentlySent, setRecentlySent] = useState(false)

  // if passcode sent successfully, show success and mark "recently sent" for 2s
  useEffect(() => {
    if (!loading && data && !error) {
      showAlert({
        msg: t('generateCodeButton.success'),
        severity: AlertType.SUCCESS,
        duration: 5000,
      })
      setRecentlySent(true)
      const timeout = setTimeout(() => {
        setRecentlySent(false)
      }, 2000)
      return () => clearTimeout(timeout)
    }
  }, [loading, data, error])

  // if error, check if it's a "too many requests" error and set countdown if so
  useEffect(() => {
    if (error) {
      // TODO differentiate between the "too many requests" error that you get
      // from requesting another code too soon vs. calling the API too
      // frequently, since users may get odd alerts from this, i.e., "wait 3
      // seconds" followed by "wait 17 seconds"
      const retry = headers?.get('Retry-After')
      if (retry) {
        // retry ends with an "s" so slice removes that
        const duration = Math.ceil(Number(retry.slice(0, -1)))
        setCountDown(duration)
        if (countDownInterval) {
          clearInterval(countDownInterval)
          setCountDownInterval(null)
        }
        setCountDownInterval(
          setInterval(() => {
            setCountDown((countDown) => countDown - 1)
          }, 1000),
        )
        showAlert({
          msg: t('tooManyRequests', { seconds: duration }),
          severity: AlertType.WARNING,
          duration: duration * 1000,
        })
      } else {
        showAlert({
          msg: error.message,
          severity: AlertType.ERROR,
          duration: 5000,
        })
      }
    }
  }, [error, headers, setCountDown, setCountDownInterval, setCountDown])

  // if countdown reaches 0, clear the interval
  useEffect(() => {
    if (countDownInterval && countDown <= 0) {
      clearInterval(countDownInterval)
      setCountDownInterval(null)
    }
  }, [countDownInterval, countDown, setCountDownInterval])

  const buttonLabel = useCallback(() => {
    switch (true) {
      // "25 seconds"
      case !!countDownInterval:
        return (
          <>
            <CircularProgress size={20} sx={{ color: theme.palette.grey[900] }} aria-hidden />
            {t('resendTimer', { seconds: countDown, count: countDown })}
          </>
        )

      // "Sent"
      case recentlySent:
        return t('generateCodeButton.recentlySent')

      // "Resend code"
      default:
        return t('generateCodeButton.resendCode')
    }
  }, [t, countDownInterval, countDown, recentlySent, theme])

  return (
    <Box>
      <Button
        data-testid="resend-code-button"
        variant="outlined"
        onClick={callFetch}
        disabled={loading || recentlySent || !!countDownInterval}
        sx={{ borderColor: theme.palette.secondary.main }}
      >
        <Typography
          variant="buttonMedium"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            justifyContent: 'space-between',
          }}
        >
          {buttonLabel()}
        </Typography>
      </Button>
    </Box>
  )
}

export default GenerateCodeButton
